import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import {
  Avatar,
  DeskEmpty,
  DeskLink,
  Eyebrow,
  NotifyToggle,
  Toggle,
  inputStyle,
  relativeDay,
  spellNumber,
} from './essayShared';

const ROLE_TAGS = { PARENT: 'Parent', ADVISOR: 'Advisor' };

// Shorter phrasings of the capabilities, for the invite panel's "they'll start
// with" list where the surrounding copy already says who "they" are.
const INVITE_BLURBS = {
  seeList: 'College list & deadlines',
  seeProgress: 'Essay stages & progress',
  comment: 'Brainstorms & comments',
};

// One-line summary of what somebody can do, for the collapsed cards.
function summarise(grant, capabilities) {
  const on = capabilities.filter((c) => grant[c.key]);
  const off = capabilities.filter((c) => !grant[c.key]);
  const shortLabel = {
    seeList: 'List & deadlines',
    seeProgress: 'progress',
    readDrafts: 'drafts',
    comment: 'comments',
    assignTasks: 'task assigning',
    emailReminders: 'reminders',
  };
  const yes = on.map((c) => shortLabel[c.key]).join(' · ');
  const no = off.length ? `no ${off.map((c) => shortLabel[c.key]).join(', ').toLowerCase()}` : null;
  return { yes: yes || 'Nothing yet', no };
}

export default function CollegeAccessPage() {
  const { api } = useAuth();
  const [grants, setGrants] = useState([]);
  const [capabilities, setCapabilities] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    let alive = true;
    api
      .get('/college-access')
      .then((res) => {
        if (!alive) return;
        const list = res.data.grants || [];
        setGrants(list);
        setCapabilities(res.data.capabilities || []);
        setEvents(res.data.events || []);
        if (list.length) setExpandedId(list[0].id);
      })
      .catch(() => {
        if (alive) toast.error('Failed to load who has access');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [api]);

  const setCapability = async (grant, key, value) => {
    const previous = grant[key];
    setGrants((prev) => prev.map((g) => (g.id === grant.id ? { ...g, [key]: value } : g)));
    try {
      const res = await api.patch(`/college-access/${grant.id}`, { [key]: value });
      setGrants((prev) => prev.map((g) => (g.id === grant.id ? res.data.grant : g)));
    } catch (err) {
      setGrants((prev) => prev.map((g) => (g.id === grant.id ? { ...g, [key]: previous } : g)));
      toast.error(err.response?.data?.error || 'Failed to change that');
    }
  };

  const remove = async (grant) => {
    if (!window.confirm(`Withdraw ${grant.name}'s access? They lose it immediately.`)) return;
    try {
      await api.delete(`/college-access/${grant.id}`);
      setGrants((prev) => prev.filter((g) => g.id !== grant.id));
      toast.success(`${grant.name} no longer has access`);
    } catch {
      toast.error('Failed to withdraw access');
    }
  };

  const invite = async (payload) => {
    const res = await api.post('/college-access', payload);
    setGrants((prev) => [...prev, res.data.grant]);
    setExpandedId(res.data.grant.id);
    if (res.data.delivery?.sent) toast.success(`Invite emailed to ${res.data.grant.email}`);
    else if (res.data.delivery?.error) toast.error("Invite created, but the email didn't send — share the link");
    else toast.success(`Invite ready for ${res.data.grant.name} — share the link`);
    return res.data.grant;
  };

  if (loading) {
    return (
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '44px 32px 64px' }}>
        <p className="font-mono-editorial" style={{ fontSize: 13, color: 'var(--college-muted)' }}>
          Loading access…
        </p>
      </div>
    );
  }

  const latestInvite = [...grants].reverse().find((g) => g.status === 'INVITED') || null;

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '36px 32px 64px' }}>
      <style>{`
        @media (max-width: 940px) {
          .desk-access-grid { grid-template-columns: minmax(0,1fr) !important; }
        }
      `}</style>

      <div style={{ borderBottom: '2px solid var(--college-ink)', paddingBottom: 16 }}>
        <Eyebrow>Your account · your call</Eyebrow>
        <h1
          className="font-serif-editorial"
          style={{ fontSize: 44, fontWeight: 400, lineHeight: 1, margin: 0, color: 'var(--college-ink)' }}
        >
          Access
        </h1>
        <p
          style={{
            margin: '14px 0 0',
            maxWidth: 700,
            fontSize: 14.5,
            lineHeight: 1.6,
            color: 'var(--college-ink-soft)',
            textWrap: 'pretty',
          }}
        >
          People you invite here see your <strong>college applications only</strong>. Your School side — coursework,
          IAs, grades, the planner — is never included, and you can withdraw any of this at any time.
        </p>
      </div>

      <div
        className="desk-access-grid"
        style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 340px', gap: 32, marginTop: 28, alignItems: 'start' }}
      >
        <div>
          <Eyebrow style={{ margin: '0 0 12px' }}>
            {grants.length === 0
              ? 'Nobody has access yet'
              : `${spellNumber(grants.length, { capital: true })} ${
                  grants.length === 1 ? 'person has' : 'people have'
                } access`}
          </Eyebrow>

          {grants.length === 0 ? (
            <DeskEmpty
              title="It's just you in here"
              body="Invite a parent or an advisor and pick exactly what they can see. Nothing from your School side ever comes with it."
            />
          ) : (
            grants.map((grant) =>
              expandedId === grant.id ? (
                <ExpandedPerson
                  key={grant.id}
                  grant={grant}
                  capabilities={capabilities}
                  onToggle={(key, value) => setCapability(grant, key, value)}
                  onRemove={() => remove(grant)}
                  onCollapse={() => setExpandedId(null)}
                />
              ) : (
                <CollapsedPerson
                  key={grant.id}
                  grant={grant}
                  capabilities={capabilities}
                  onExpand={() => setExpandedId(grant.id)}
                />
              )
            )
          )}

          {events.length > 0 && (
            <div style={{ marginTop: 28 }}>
              <Eyebrow style={{ margin: '0 0 12px' }}>Recent activity</Eyebrow>
              <div style={{ background: 'var(--college-bg-alt)', border: '1px solid var(--college-border-soft)' }}>
                {events.map((event, i) => (
                  <div
                    key={event.id}
                    style={{
                      display: 'flex',
                      gap: 14,
                      padding: '11px 16px',
                      borderBottom: i === events.length - 1 ? 'none' : '1px solid var(--college-border-soft)',
                    }}
                  >
                    <span
                      className="font-mono-editorial"
                      style={{ fontSize: 11.5, color: 'var(--college-muted-2)', minWidth: 62, flexShrink: 0 }}
                    >
                      {relativeDay(event.createdAt)}
                    </span>
                    <span style={{ fontSize: 12.5, color: 'var(--college-ink-soft)' }}>{event.summary}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <InvitePanel capabilities={capabilities} onInvite={invite} latestInvite={latestInvite} />
      </div>
    </div>
  );
}

function PersonHeader({ grant, onRemove, right }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 13,
        padding: '16px 18px',
        borderBottom: '1px solid var(--college-border-soft)',
        background: 'var(--college-bg-alt)',
        flexWrap: 'wrap',
      }}
    >
      <Avatar person={{ ...grant, kind: 'grant' }} size={38} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
          <span className="font-serif-editorial" style={{ fontSize: 19, color: 'var(--college-ink)' }}>
            {grant.name}
          </span>
          <span
            style={{
              padding: '2px 8px',
              border: '1px solid var(--college-ink)',
              fontSize: 9.5,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--college-ink)',
            }}
          >
            {ROLE_TAGS[grant.role]}
          </span>
          {grant.status === 'INVITED' && (
            <span
              style={{
                padding: '2px 8px',
                background: 'var(--college-bg-alt)',
                border: '1px solid var(--college-border)',
                fontSize: 9.5,
                fontWeight: 600,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: 'var(--college-ink-soft)',
              }}
            >
              Invited {relativeDay(grant.createdAt)}
            </span>
          )}
        </div>
        <p className="font-mono-editorial" style={{ margin: '3px 0 0', fontSize: 11.5, color: 'var(--college-muted)' }}>
          {grant.email}
          {grant.status === 'ACTIVE' ? ' · active' : ' · invite pending'}
          {grant.lastOpenedAt ? ` · last opened ${relativeDay(grant.lastOpenedAt)}` : ''}
        </p>
      </div>
      {right}
      {onRemove && <DeskLink onClick={onRemove}>Remove</DeskLink>}
    </div>
  );
}

function ExpandedPerson({ grant, capabilities, onToggle, onRemove, onCollapse }) {
  return (
    <div style={{ background: 'var(--college-panel)', border: '1px solid var(--college-border)', marginBottom: 14 }}>
      <PersonHeader
        grant={grant}
        onRemove={onRemove}
        right={
          <DeskLink onClick={onCollapse} style={{ color: 'var(--college-muted)' }}>
            Collapse ▴
          </DeskLink>
        }
      />
      <div style={{ padding: '6px 0' }}>
        {capabilities.map((capability, i) => (
          <div
            key={capability.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              padding: '12px 18px',
              borderBottom: i === capabilities.length - 1 ? 'none' : '1px solid #F0EADC',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: 13.5, color: 'var(--college-ink)' }}>{capability.label}</p>
              {capability.hint && (
                <p style={{ margin: '3px 0 0', fontSize: 11.5, color: 'var(--college-muted)' }}>{capability.hint}</p>
              )}
            </div>
            <Toggle
              on={!!grant[capability.key]}
              label={capability.label}
              onChange={(value) => onToggle(capability.key, value)}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function CollapsedPerson({ grant, capabilities, onExpand }) {
  const { yes, no } = summarise(grant, capabilities);
  return (
    <div
      style={{
        background: 'var(--college-panel)',
        border: '1px solid var(--college-border-soft)',
        display: 'flex',
        alignItems: 'center',
        gap: 13,
        padding: '15px 18px',
        marginBottom: 12,
        flexWrap: 'wrap',
      }}
    >
      <Avatar person={{ ...grant, kind: 'grant' }} size={34} />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
          <span className="font-serif-editorial" style={{ fontSize: 18, color: 'var(--college-ink)' }}>
            {grant.name}
          </span>
          <span
            style={{
              padding: '2px 8px',
              border: '1px solid var(--college-border)',
              fontSize: 9.5,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--college-muted)',
            }}
          >
            {ROLE_TAGS[grant.role]}
          </span>
          {grant.status === 'INVITED' && (
            <span
              style={{
                padding: '2px 8px',
                background: 'var(--college-bg-alt)',
                fontSize: 9.5,
                fontWeight: 600,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: 'var(--college-ink-soft)',
              }}
            >
              Invited {relativeDay(grant.createdAt)}
            </span>
          )}
        </div>
        <p style={{ margin: '3px 0 0', fontSize: 12, color: 'var(--college-muted)' }}>
          {yes}
          {no ? (
            <>
              {' — '}
              <span style={{ color: 'var(--college-muted-2)' }}>{no}</span>
            </>
          ) : null}
        </p>
      </div>
      <DeskLink onClick={onExpand}>Edit ▾</DeskLink>
    </div>
  );
}

function InvitePanel({ capabilities, onInvite, latestInvite }) {
  const [role, setRole] = useState('PARENT');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [sending, setSending] = useState(false);
  const [notify, setNotify] = useState(false);
  const [link, setLink] = useState(null);

  useEffect(() => {
    if (latestInvite) setLink(`${window.location.origin}/invite/${latestInvite.inviteToken}`);
  }, [latestInvite]);

  const send = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('An email address, please');
      return;
    }
    setSending(true);
    try {
      const grant = await onInvite({ email, name, role, notify, inviteOrigin: window.location.origin });
      setLink(`${window.location.origin}/invite/${grant.inviteToken}`);
      setEmail('');
      setName('');
      setNotify(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send that invite');
    } finally {
      setSending(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success('Link copied');
    } catch {
      toast.error('Copy it by hand — the clipboard said no');
    }
  };

  // What a brand-new invite comes with, in the invite panel's shorter voice.
  const startsWith = capabilities
    .filter((c) => ['seeList', 'seeProgress', 'comment'].includes(c.key))
    .map((c) => ({ key: c.key, label: INVITE_BLURBS[c.key] }));
  const laterOn = capabilities.filter((c) => c.key === 'readDrafts');

  return (
    <form onSubmit={send} style={{ background: 'var(--college-ink)', color: 'var(--college-bg)', padding: '24px 22px' }}>
      <p
        style={{
          font: "600 11px/1 'Instrument Sans', sans-serif",
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: 'rgba(245,240,230,0.55)',
          margin: '0 0 12px',
        }}
      >
        Invite someone
      </p>
      <h3
        className="font-serif-editorial"
        style={{ fontSize: 26, fontWeight: 400, margin: '0 0 18px', lineHeight: 1.15 }}
      >
        Bring in a parent or an advisor
      </h3>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {['PARENT', 'ADVISOR'].map((option) => {
          const active = role === option;
          return (
            <button
              key={option}
              type="button"
              onClick={() => setRole(option)}
              style={{
                flex: 1,
                textAlign: 'center',
                padding: '8px 0',
                border: active ? 'none' : '1px solid rgba(245,240,230,0.3)',
                background: active ? 'var(--college-bg)' : 'transparent',
                color: active ? 'var(--college-ink)' : 'rgba(245,240,230,0.8)',
                fontFamily: 'inherit',
                fontSize: 12,
                fontWeight: active ? 600 : 400,
                cursor: 'pointer',
              }}
            >
              {ROLE_TAGS[option]}
            </button>
          );
        })}
      </div>

      <p style={{ margin: '0 0 7px', fontSize: 11.5, color: 'rgba(245,240,230,0.6)' }}>Their email</p>
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="name@example.com"
        style={{
          ...inputStyle,
          border: 'none',
          borderBottom: '1px solid rgba(245,240,230,0.35)',
          background: 'transparent',
          color: 'var(--college-bg)',
          padding: '0 0 8px',
          marginBottom: 16,
          fontSize: 14,
        }}
      />

      <p style={{ margin: '0 0 7px', fontSize: 11.5, color: 'rgba(245,240,230,0.6)' }}>Their name (optional)</p>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Priya Chen"
        style={{
          ...inputStyle,
          border: 'none',
          borderBottom: '1px solid rgba(245,240,230,0.35)',
          background: 'transparent',
          color: 'var(--college-bg)',
          padding: '0 0 8px',
          marginBottom: 18,
          fontSize: 14,
        }}
      />

      <p style={{ margin: '0 0 10px', fontSize: 11.5, color: 'rgba(245,240,230,0.6)' }}>They'll start with</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginBottom: 20 }}>
        {startsWith.map((c) => (
          <span key={c.key} style={{ fontSize: 13 }}>
            ✓ &nbsp;{c.label}
          </span>
        ))}
        {laterOn.map((c) => (
          <span key={c.key} style={{ fontSize: 13, color: 'rgba(245,240,230,0.45)' }}>
            — &nbsp;Drafts (you can turn this on later)
          </span>
        ))}
      </div>

      <div style={{ marginBottom: 16 }}>
        <NotifyToggle
          checked={notify}
          onChange={setNotify}
          who={email.trim() ? [email.trim()] : []}
          style={{ color: 'rgba(245,240,230,0.8)' }}
        />
      </div>

      <button
        type="submit"
        disabled={sending}
        style={{
          display: 'block',
          width: '100%',
          textAlign: 'center',
          padding: '11px 0',
          border: 'none',
          background: 'var(--college-gold)',
          color: 'var(--college-ink)',
          fontFamily: 'inherit',
          fontSize: 13,
          fontWeight: 700,
          cursor: sending ? 'default' : 'pointer',
          opacity: sending ? 0.6 : 1,
        }}
      >
        {sending ? 'Sending…' : 'Send invite'}
      </button>

      {link && (
        <div style={{ marginTop: 22, paddingTop: 18, borderTop: '1px solid rgba(245,240,230,0.2)' }}>
          <p style={{ margin: '0 0 9px', fontSize: 11.5, color: 'rgba(245,240,230,0.6)' }}>Or share a link</p>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              background: 'rgba(245,240,230,0.08)',
              padding: '10px 12px',
            }}
          >
            <span
              className="font-mono-editorial"
              style={{ fontSize: 11.5, color: 'var(--college-bg)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {link.replace(/^https?:\/\//, '')}
            </span>
            <DeskLink onClick={copy} style={{ color: 'var(--college-gold)', fontSize: 11.5 }}>
              Copy
            </DeskLink>
          </div>
          <p style={{ margin: '9px 0 0', fontSize: 11, color: 'rgba(245,240,230,0.5)' }}>
            Expires in 7 days · single use · they confirm who they are before it works
          </p>
        </div>
      )}
    </form>
  );
}
